import type { PasskeyAnswer } from '@trippath/shared';

/** Bytes from base64url, as the server sends a challenge or an id. */
export function fromBase64url(text: string): ArrayBuffer {
  const plain = text.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(plain + '='.repeat((4 - (plain.length % 4)) % 4));
  return Uint8Array.from(raw, (letter) => letter.charCodeAt(0)).buffer;
}

/** Bytes as base64url, as the server takes them. */
export function toBase64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Whether this browser makes and uses passkeys at all. */
export function passkeysHere(): boolean {
  return typeof window.PublicKeyCredential === 'function';
}

type Said = Record<string, unknown>;

const textOf = (said: Said, key: string): string => {
  const value = said[key];
  return typeof value === 'string' ? value : '';
};

const partOf = (said: Said, key: string): Said => {
  const value = said[key];
  return typeof value === 'object' && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};
};

const listOf = (said: Said, key: string): Said[] => {
  const value = said[key];
  return Array.isArray(value) ? value.map((item: unknown) => partOf({ item }, 'item')) : [];
};

/** The answer was not a passkey: the browser gave something else, or nothing. */
export class NoPasskey extends Error {
  constructor() {
    super('no passkey');
    this.name = 'NoPasskey';
  }
}

/**
 * Asks the browser to make a passkey with the server's options (ECDSA over P-256, kept on the
 * device): its id, client data and attestation, each in base64url. Rejected when the person
 * cancels.
 */
export async function makePasskey(
  options: Said,
): Promise<{ id: string; clientData: string; attestation: string }> {
  const party = partOf(options, 'rp');
  const user = partOf(options, 'user');
  const made = await navigator.credentials.create({
    publicKey: {
      challenge: fromBase64url(textOf(options, 'challenge')),
      rp: { id: textOf(party, 'id'), name: textOf(party, 'name') },
      user: {
        id: fromBase64url(textOf(user, 'id')),
        name: textOf(user, 'name'),
        displayName: textOf(user, 'displayName'),
      },
      pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
      timeout: 300_000,
      attestation: 'none',
      authenticatorSelection: { residentKey: 'required', userVerification: 'preferred' },
      excludeCredentials: listOf(options, 'excludeCredentials').map((known) => ({
        type: 'public-key',
        id: fromBase64url(textOf(known, 'id')),
      })),
    },
  });
  if (
    !(made instanceof PublicKeyCredential) ||
    !(made.response instanceof AuthenticatorAttestationResponse)
  ) {
    throw new NoPasskey();
  }
  return {
    id: toBase64url(made.rawId),
    clientData: toBase64url(made.response.clientDataJSON),
    attestation: toBase64url(made.response.attestationObject),
  };
}

/** Asks the browser to sign in with a passkey of this site, any on the device. */
export async function signWithPasskey(options: Said): Promise<PasskeyAnswer> {
  const used = await navigator.credentials.get({
    publicKey: {
      challenge: fromBase64url(textOf(options, 'challenge')),
      rpId: textOf(options, 'rpId'),
      timeout: 300_000,
      userVerification: 'preferred',
      allowCredentials: [],
    },
  });
  if (
    !(used instanceof PublicKeyCredential) ||
    !(used.response instanceof AuthenticatorAssertionResponse)
  ) {
    throw new NoPasskey();
  }
  return {
    id: toBase64url(used.rawId),
    clientData: toBase64url(used.response.clientDataJSON),
    authenticator: toBase64url(used.response.authenticatorData),
    signature: toBase64url(used.response.signature),
  };
}

/** A browser said by its user agent, `Chrome · Linux`; `null` for one not known. */
export function browserOf(agent: string | null): string | null {
  if (agent === null) return null;
  const browsers: [string, string][] = [
    ['Edg/', 'Edge'],
    ['OPR/', 'Opera'],
    ['Firefox/', 'Firefox'],
    ['Chrome/', 'Chrome'],
    ['Safari/', 'Safari'],
  ];
  const systems: [string, string][] = [
    ['Android', 'Android'],
    ['iPhone', 'iPhone'],
    ['iPad', 'iPad'],
    ['Windows', 'Windows'],
    ['Mac OS', 'macOS'],
    ['CrOS', 'ChromeOS'],
    ['Linux', 'Linux'],
  ];
  const said = [browsers, systems].flatMap((known) =>
    known
      .filter(([mark]) => agent.includes(mark))
      .slice(0, 1)
      .map(([, name]) => name),
  );
  return said.length === 0 ? null : said.join(' · ');
}
